import React, { useEffect, useMemo, useState } from "react";
import { getFromStorage } from "../../../utils/imageUtil";
import { useTranslation } from "react-i18next";
import Balance from "../Balance/Balance";
import { tonDepositApi, tonHistoryApi, tonWithdrawApi } from "../../../api/apiFunctions";
import { ConnectedWallet, SendTransactionRequest, TonConnectButton, useTonConnectUI } from "@tonconnect/ui-react";
import { IUserModel, TransactionModel } from "../../../types/types";
import ProgressLoader from "../ProgressLoader/ProgressLoader";
import ModalWindow from "../ModalWindow/ModalWindow";
import value from "*.png";

interface WalletProps {
  user: IUserModel;
  setUser: (user: IUserModel) => void;
  onCloseModal: () => void;
}

const Wallet = ({user, setUser, onCloseModal}: WalletProps) => {
  const [t] = useTranslation();
  const [tonConnectUI] = useTonConnectUI();
  const [transactions, setTransactions] = useState<TransactionModel[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [input, setInput] = useState('1');
  const [amount, setAmount] = useState(1e9);
  const [isDeposit, setIsDeposit] = useState<boolean | null>(null);

  const handleChange = (e: any) => {
    const amount = parseFloat(e.target.value).toFixed(2);
    setInput(amount)
    console.log("TON amount:", amount);
    const nanoTon = Math.floor(parseFloat(amount) * 1e9);
    setAmount(nanoTon);
    console.log("nanoTON:", nanoTon);
  };

  const formatAddress = (address: string) => {
    if (!address) return '';
    return `${ address.slice(0, 5) }...${ address.slice(-4) }`;
  };

  const onCloseModalButtonClick = () => {
    onCloseModal();
    setModalOpen(false);
  }

  const onReturnButtonClick = () => {
    setModalOpen(false);
  }

  const onSendTransactionClick = (isDeposit: boolean) => {
    if (tonConnectUI && tonConnectUI.wallet?.account.address) {
      try {
        setLoading(true);
        setIsDeposit(isDeposit);
        setValuesToDefault();
        setModalOpen(true);
      } finally {
        setLoading(false);
      }
    }
  }

  const sendTransaction = async () => {
    onReturnButtonClick();
    if (amount < 1e9) return;

    try {
      setLoading(true);
      const transactionModel = await tonDepositApi();
      const transaction: SendTransactionRequest = {
        validUntil: Date.now() + 5 * 60 * 1000, // 5 minutes
        messages: [
          {
            address: transactionModel.destinationAddress,
            amount: amount.toString(),
            payload: transactionModel.id.toString(),
          },
        ],
      };
      await tonConnectUI.sendTransaction(transaction);
      await fetchTransactions();
    } catch (e) {
      console.log(e);
    } finally {
      setModalOpen(false);
      setIsDeposit(null);
      setValuesToDefault();
      setLoading(false);
    }
  }

  const sendWithdrawRequest = async () => {
    onReturnButtonClick();
    if (amount < 1e9 || !tonConnectUI.wallet?.account.address) return;

    try {
      setLoading(true);
      await tonWithdrawApi({address: tonConnectUI.wallet?.account.address, amount: amount});
      await fetchTransactions();
    } finally {
      setModalOpen(false);
      setValuesToDefault();
      setLoading(false);
    }
  }

  const setValuesToDefault = useMemo(() => {
    return () => {
      setInput("1");
      setAmount(1e9);
    }
  }, [input, amount, setAmount, setInput])

  const fetchTransactions = async () => {
    if (tonConnectUI.wallet?.account.address) {
      try {
        setLoading(true);
        const transactionModels = await tonHistoryApi();
        setTransactions(transactionModels);
      } finally {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    fetchTransactions();
  }, [tonConnectUI.wallet?.account.address]);

  return (
    <>
      { loading ? (<ProgressLoader/>) : (
        <>
          <div className={ 'flex justify-center my-4' }>
            <TonConnectButton/>
          </div>
          {
            <>
              <div className="overflow-y px-4 grow flex flex-col gap-4 font-mono">
                <div className="w-full flex flex-col justify-center gap-6 grow">
                  <div className={ 'flex gap-4 mb-4 mt-2 justify-center items-center font-bold' }>
                    <Balance
                      src={ getFromStorage('ton.svg') }
                      userCoins={ user.tonBalance }
                      alt={ 'ton' }
                      imgClass={ 'h-12 w-12' }
                      textClass={ 'font-semibold text-4xl font-bold leading-10' }
                    ></Balance>
                  </div>
                </div>
                <div className="flex w-auto gap-4 justify-around">
                  <div
                    className={ 'h-12 w-12 bg-gray-700 rounded-full overflow-hidden flex items-center justify-center font-bold text-4xl cursor-pointer' }
                    onClick={ () => onSendTransactionClick(true) }>&uarr;</div>
                  <div onClick={ () => onSendTransactionClick(false) }
                       className={ 'h-12 w-12 bg-gray-700 rounded-full overflow-hidden flex items-center justify-center font-bold text-4xl cursor-pointer' }>&darr;</div>
                  <div className={ 'h-16 w-16 bg-transparent rounded-full overflow-hidden' }></div>
                  <div className={ 'h-16 w-16 bg-transparent rounded-full overflow-hidden' }></div>
                </div>
              </div>
              <div className="grow border-t-gray-600 border-t mx-4"></div>
              <ul className="space-y-2 p-4 flex flex-col overflow-y-auto font-mono">
                { transactions && transactions.length > 0 ? (
                  transactions.map((transaction: TransactionModel, index: number) => {
                    const isPending = !transaction.txnHash;
                    const displayAddress = transaction.direction === "INCOMING" ? transaction.sourceAddress : transaction.destinationAddress;
                    const displayAmount = transaction.amount ? `${ transaction.amount } TON` : null;
                    const displayDate = isPending
                      ? new Date(Number(transaction.createdAt) * 1000) // createdAt in seconds
                      : new Date(Number(transaction.processedAt) * 1000); // processedAt in seconds

                    return (
                      <li key={ index } className="bg-gray-800 p-2 rounded-lg border border-gray-700 shadow-sm">
                        <div className="flex justify-between items-center text-xs">
                          <div className="text-gray-400">
                            <span>{ displayDate.toLocaleDateString() + ', ' + displayDate.toLocaleTimeString() }</span>
                          </div>
                          <div>
                              <span
                                className={ `text-xs ${ transaction.direction === "INCOMING" ? "text-green-400" : "text-red-400" }` }>
                                { transaction.direction }
                              </span>
                          </div>
                        </div>

                        <div className="mt-1 text-sm text-gray-300">
                          <div>
                              <span
                                className="text-xs text-gray-400">{ transaction.direction === "INCOMING" ? t('app.wallet.from') : t('app.wallet.to') }</span>
                            <span className="font-medium text-gray-200">{ formatAddress(displayAddress) }</span>
                          </div>
                        </div>

                        { displayAmount && (
                          <div className="mt-1 text-xs text-gray-300">
                            <span className="text-xs text-gray-400">{ t('app.wallet.amount') }</span>
                            <span className="font-semibold text-yellow-400">{ displayAmount }</span>
                          </div>
                        ) }

                        <div className="mt-1 flex justify-between items-center text-xs text-gray-300">
                          { isPending ? (
                            <span className="text-yellow-400">{ t('app.wallet.pending') }</span>
                          ) : (
                            <a
                              href={ `https://tonscan.org/tx/${ transaction.txnHash }` }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-500 hover:underline"
                            >
                              View
                            </a>
                          ) }
                        </div>
                      </li>
                    );
                  })
                ) : (
                  <li className="text-gray-500 text-sm">No transactions available.</li>
                ) }
              </ul>
            </>
          }
        </>
      ) }
      <ModalWindow isShow={ modalOpen } className={ 'min-w-full' }
                   header={ t(isDeposit !== null ? isDeposit ? 'app.wallet.deposit' : 'app.wallet.withdraw' : '') }
                   onCloseButtonClick={ onCloseModalButtonClick }>
        <div className={ 'flex flex-col items-center w-full h-full p-4' }>
          <div className={ 'w-full' }>
            <button onClick={ onReturnButtonClick }>{ '< ' + t('app.wallet.return') }</button>
          </div>
          <div className={ 'grow' }></div>
          <div className={ 'flex flex-col w-auto gap-1' }>
            <label htmlFor="{'amount-input'}" className={ 'text-lg' }>{ t('app.wallet.enter.amount') }</label>
            <input
              id={ 'amount-input' }
              type="number"
              min={ isDeposit ? '1' : '0' }
              step="0.01"
              max={ isDeposit ? Infinity : user.tonBalance }
              value={ isDeposit ? input : user.tonBalance > 1 ? input : user.tonBalance }
              onChange={ handleChange }
              className="w-48 px-4 py-2 text-xl text-yellow-400 bg-gray-700 rounded-2xl border
              border-gray-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 shadow-sm transition-all
              appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
          <div className={ 'grow' }></div>
          <div className={ 'grow' }></div>
          <div className={ 'grow' }></div>
          <div className={ 'grow' }></div>
          <div className={ 'flex gap-2 w-full' }>
            <button onClick={ isDeposit ? sendTransaction : sendWithdrawRequest }
                    className={ 'bg-green-700 py-3 rounded-2xl w-full' }>{ t('app.wallet.confirm') }</button>
            <button onClick={ onReturnButtonClick }
                    className={ 'bg-red-700 py-3 rounded-2xl w-full' }>{ t('app.wallet.cancel') }</button>
          </div>
        </div>
      </ModalWindow>
    </>
  );
};

export default Wallet;
